// ---------------------------------------------------------------------------
// A binding that follows a signal by itself (R12d)
//
// watch(signal) renders the signal's value and writes the part again
// whenever it changes, without a render of the template around it. The
// frame needs that for its own state (the theme shown in both navs, the
// drawer, the sign-out run): the frame is rendered by the app root's
// stateful(), which re-renders on the signals it read, but the root renders
// the frame through Adornis's session guard (app-root.ts), and the guard
// draws the outlet again from its own user feed, outside the root's render.
// What the frame read there, nothing would track: after sign-in a theme pick
// would change <html> and leave both controls showing the old choice until
// the next page change. A watched binding does not depend on who rendered it.
//
// Lit's own @lit-labs/preact-signals has this directive; it is not a
// dependency of this package, and these twenty lines are all it takes on
// the @preact/signals-core the router already uses (decision c5).
// ---------------------------------------------------------------------------

import { effect, type ReadonlySignal, untracked } from '@preact/signals-core';
import { AsyncDirective } from 'lit/async-directive.js';
import { directive } from 'lit/directive.js';

class Watch extends AsyncDirective {
  private signal: ReadonlySignal<unknown> | undefined;
  private stop: (() => void) | undefined;

  override render(signal: ReadonlySignal<unknown>): unknown {
    return signal.peek();
  }

  override update(_part: unknown, [signal]: [ReadonlySignal<unknown>]): unknown {
    if (signal !== this.signal) {
      this.stop?.();
      this.stop = undefined;
      this.signal = signal;
    }
    if (!this.stop && this.isConnected) this.listen();
    return signal.peek();
  }

  private listen() {
    const signal = this.signal;
    if (!signal) return;
    let first = true;
    this.stop = effect(() => {
      const value = signal.value;
      if (first) {
        first = false;
        return;
      }
      // What the new value renders is not this binding's to track.
      untracked(() => this.setValue(value));
    });
  }

  protected override disconnected() {
    this.stop?.();
    this.stop = undefined;
  }

  protected override reconnected() {
    this.listen();
    if (this.signal) this.setValue(this.signal.peek());
  }
}

/** Renders a signal's value, and again whenever it changes. */
export const watch = directive(Watch);
